import React from 'react';

// The user backend keeps what a document check read when the customer
// corrects it in the new app (POST /case/{id}/corrections): each file carries
// corrections.<field> = { read, value }. The case fields and the POA already
// use the customer's value; this shows the lawyer what changed.
type Correction = { read?: unknown; value?: unknown };

const DOCUMENT_LABELS: Record<string, string> = {
  proof_of_identity: 'Identity document',
  proof_of_address: 'Proof of address',
  birth_certificate: 'Birth certificate',
};

const FIELD_LABELS: Record<string, string> = {
  first_name: 'Given names',
  middle_name: 'Middle name',
  last_name: 'Surname',
  birthdate: 'Date of birth',
  nationality_iso: 'Nationality',
  id_passport_number: 'Document number',
  id_passport_expiry_date: 'Expiry date',
  place_of_birth: 'Place of birth',
  street: 'Street',
  postal_code: 'Postcode',
  city: 'City',
  country_iso: 'Country',
  child_first_name: "Child's given names",
  child_middle_name: "Child's middle name",
  child_last_name: "Child's surname",
  child_birthdate: "Child's date of birth",
  parent_names: 'Parents',
  certificate_number: 'Certificate number',
  issuing_authority: 'Issued by',
  issue_date: 'Issued on',
};

const FINISHED_STATUSES = new Set(['Complete', 'Closed']);

interface CorrectedDocument {
  fileType?: string;
  guardianIndex?: number | string;
  corrections?: Record<string, Correction> | null;
  [key: string]: any;
}

export interface CorrectionLine {
  key: string;
  document: string;
  field: string;
  read: string;
  value: string;
}

const shown = (v: unknown): string => {
  if (Array.isArray(v)) return v.filter(Boolean).join(', ');
  return typeof v === 'string' && v.trim() ? v : 'nothing';
};

const documentLabel = (doc: CorrectedDocument): string => {
  if (doc.fileType === 'guardian_identity') return doc.guardianIndex ? `Parent ${doc.guardianIndex}'s identity document` : "A parent's identity document";
  return DOCUMENT_LABELS[doc.fileType ?? ''] ?? doc.fileType ?? 'Document';
};

/** One line per corrected field; an ID card's two sides carry the same correction once. */
export function correctionLines(documents: CorrectedDocument[] = []): CorrectionLine[] {
  const lines = new Map<string, CorrectionLine>();
  for (const doc of documents) {
    for (const [field, c] of Object.entries(doc.corrections ?? {})) {
      if (!c || typeof c !== 'object') continue;
      // The new app edits given names as one field, so an emptied middle name only moved into them.
      if ((field === 'middle_name' || field === 'child_middle_name') && shown(c.value) === 'nothing') continue;
      const key = `${doc.fileType}:${doc.guardianIndex ?? ''}:${field}`;
      lines.set(key, { key, document: documentLabel(doc), field: FIELD_LABELS[field] ?? field, read: shown(c.read), value: shown(c.value) });
    }
  }
  return Array.from(lines.values());
}

interface Props {
  documents?: CorrectedDocument[];
  lawyerStatus?: string;
}

const CustomerCorrectionsBanner: React.FC<Props> = ({ documents, lawyerStatus }) => {
  const lines = correctionLines(documents);
  if (!lines.length || FINISHED_STATUSES.has(lawyerStatus ?? '')) return null;
  return (
    <div className="final-doc-warning poa-warnings-banner" data-testid="customer-corrections">
      <strong>Corrected by the customer</strong>
      <p className="poa-warnings-intro">
        The customer changed what our check read off these documents. The case and the POA use the customer's value. Compare it with the document before submitting.
      </p>
      <ul className="poa-warnings-list">
        {lines.map((l) => (
          <li key={l.key}>
            {l.document}, {l.field}: read "{l.read}", customer "{l.value}"
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CustomerCorrectionsBanner;
